import { ArrowUpRight, ShieldCheck, Banknote, Wrench, Scale, Check, Car } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuoteModal } from "../lib/quoteModal";
import { CTA_INSURANCE, CTA_AUTO_LOAN, CTA_WARRANTY } from "../lib/ctaVocabulary";
import { ProviderAppTile } from "./ProviderAppTile";
import { VERIFIED_INSURERS } from "../lib/verifiedInsurers";

const CARDS = [
  {
    id: "insurance",
    eyebrow: "01 / Auto Insurance",
    title: "Compare auto insurance and pay less for the same coverage.",
    body: "Compare personalized quotes from top U.S. insurers in just a few minutes. See how rates vary for your ZIP, vehicle, and driver profile — with no spam and no obligation.",
    cta: CTA_INSURANCE,
    Icon: ShieldCheck,
    // null href triggers the global Quote Modal instead of navigating
    href: null,
    modalCategory: "insurance",
    accent: true,
    span: "lg:col-span-7 lg:row-span-2",
    checklist: [
      "Compare rates from 120+ auto insurers",
      "$1,100 average potential savings per year, per Insurify (not guaranteed)",
      "Personalized rates for your vehicle and driver profile",
      "Free to compare with no obligation",
    ],
  },
  {
    id: "auto-loans",
    eyebrow: "02 / Auto Loans",
    title: "Lower your APR on a new, used, or refinance loan.",
    body: "Pre-qualified offers from top auto lenders with a soft credit pull. Lock in a lower rate before the dealer sees your file — and walk in with leverage.",
    cta: CTA_AUTO_LOAN,
    Icon: Car,
    href: null,
    modalCategory: "auto-loan-new",
    span: "lg:col-span-5",
  },
  {
    id: "warranty",
    eyebrow: "03 / Warranties",
    title: "Buy the warranty direct — not at the dealer.",
    body: "A Vehicle Service Contract bought direct from the provider is often cheaper than a dealer-sold plan, because dealer pricing can include a markup. Compare coverage and price before you sign.",
    cta: CTA_WARRANTY,
    Icon: Wrench,
    href: null,
    modalCategory: "warranty",
    span: "lg:col-span-5",
  },
  {
    id: "compare",
    eyebrow: "04 / Compare",
    title: "Run any car-money decision side-by-side.",
    body: "Lease vs. finance. Refinance vs. trade-in. New vs. used. Editor-graded head-to-heads with real math, total cost, and the driver profile each option fits best.",
    cta: "See Comparisons",
    Icon: Scale,
    href: "/compare",
    // Iter-68: full-width on its own row — previously left a col-5 empty
    // slot to the right of the Compare card which made the bento feel
    // unbalanced. col-span-12 lets the card breathe + visually closes the
    // section.
    span: "lg:col-span-12",
    wide: true,
  },
];

export default function CategoryBento() {
  const { open: openQuoteModal } = useQuoteModal();
  return (
    <section className="bg-[#FAFAFA] py-24 lg:py-32">
      <div className="max-w-wide mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 lg:mb-14 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-emerald-strong mb-4">
              What we help with
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-slate-950 max-w-3xl">
              Four ways to keep more of your money.
            </h2>
          </div>
          <div className="lg:col-span-4">
            {/* Iter-220 — Supporting copy carries the four priority
                keyword themes (compare auto insurance quotes / best
                auto loans / auto loan refinance / extended car
                warranty) naturally, without touching the brand-voice
                H2 above. */}
            <p className="text-slate-600 text-lg leading-relaxed lg:pb-1 text-pretty">
              Compare auto insurance quotes, find the best auto loans, run the
              auto loan refinance math, and price an extended car warranty — we
              show where real savings may exist.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 lg:grid-rows-[1fr_1fr_auto] gap-6 lg:gap-8">
          {CARDS.map(({ id, eyebrow, title, body, cta, Icon, accent, span, href, modalCategory, checklist, wide }) => {
            // Bento cards are either internal navigations (auto-loans, warranty,
            // compare hubs) OR they open the global Quote Modal in place. The
            // modal variant is keyed off `href === null` so we never accidentally
            // route the user away from the homepage.
            const isModalCard = !href;
            const commonClassName = `group relative ${span} ${
              accent ? "bg-slate-950 text-white" : "bg-white"
            } border border-slate-200 p-8 lg:p-10 flex flex-col justify-between min-h-[280px] transition-all hover:-translate-y-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#10B981] focus-visible:ring-offset-2 ${
              wide ? "lg:grid lg:grid-cols-12 lg:gap-x-12 lg:content-start lg:min-h-0" : ""
            }`;
            const Inner = (
              <CategoryCardInner
                eyebrow={eyebrow}
                title={title}
                body={body}
                cta={cta}
                Icon={Icon}
                accent={accent}
                checklist={checklist}
                wide={wide}
              />
            );
            if (isModalCard) {
              return (
                <button
                  type="button"
                  key={id}
                  id={id}
                  data-testid={`category-card-${id}`}
                  onClick={() => openQuoteModal(modalCategory)}
                  className={commonClassName}
                >
                  {Inner}
                </button>
              );
            }
            return (
              <Link
                key={id}
                id={id}
                data-testid={`category-card-${id}`}
                to={href}
                className={commonClassName}
              >
                {Inner}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Card visual extracted so the same markup renders inside either <button> or
// <Link> — keeps both code paths identical without duplicating ~40 lines of JSX.
function CategoryCardInner({ eyebrow, title, body, cta, Icon, accent, checklist, wide }) {
  return (
    <>
              <div className={wide ? "lg:contents" : undefined}>
                <div className={`flex items-center justify-between mb-8 ${wide ? "lg:col-span-12" : ""}`}>
                  <span
                    className={`text-xs uppercase tracking-[0.25em] font-bold ${
                      accent ? "text-[#10B981]" : "text-brand-emerald-strong"
                    }`}
                  >
                    {eyebrow}
                  </span>
                  <Icon
                    className={accent ? "w-7 h-7 text-[#10B981]" : "w-7 h-7 text-slate-950"}
                    strokeWidth={1.5}
                  />
                </div>
                <h3
                  className={`font-display font-black tracking-tight leading-[1.02] ${
                    accent ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"
                  } ${wide ? "lg:col-span-8" : ""}`}
                >
                  {title}
                </h3>
                <p
                  className={`mt-4 max-w-md leading-relaxed ${
                    accent ? "text-slate-300" : "text-slate-600"
                  } ${wide ? "lg:col-span-8 lg:max-w-2xl" : ""}`}
                >
                  {body}
                </p>

                {checklist && (
                  <ul
                    data-testid="category-card-checklist"
                    className="mt-8 pt-8 border-t border-white/10 space-y-3 lg:space-y-4 max-w-md"
                  >
                    {checklist.map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-sm text-slate-200 leading-snug"
                      >
                        <span className="mt-0.5 w-5 h-5 bg-[#10B981] grid place-items-center shrink-0">
                          <Check className="w-3 h-3 text-[#0A2540]" strokeWidth={3} />
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {accent && (
                  <div
                    data-testid="insurance-partners-strip"
                    className="mt-8 lg:mt-10 pt-6 lg:pt-8 border-t border-white/10"
                  >
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-300 mb-3 lg:mb-4">
                      Insurance companies you can compare
                    </p>
                    <ul className="flex flex-wrap items-start gap-3" aria-label="Insurance companies you can compare">
                      {VERIFIED_INSURERS.map((ins) => (
                        <li key={ins.slug}>
                          <ProviderAppTile insurer={ins} state="active" size="sm" labelClass="text-slate-300" />
                        </li>
                      ))}
                      <li className="self-center text-sm font-semibold text-[#10B981]">+100 more</li>
                    </ul>
                  </div>
                )}
              </div>
              <div
                className={`mt-8 inline-flex items-center gap-2 font-bold ${
                  accent ? "text-[#10B981]" : "text-slate-950"
                } ${wide ? "lg:mt-0 lg:col-start-9 lg:col-span-4 lg:row-start-2 lg:row-span-2 lg:self-end lg:justify-self-end lg:border lg:border-slate-200 lg:px-5 lg:py-3 lg:group-hover:border-slate-950 lg:transition-colors" : ""}`}
              >
                {cta}
                <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
    </>
  );
}
